import React from "react";
import { Card } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Switch } from "@/components/ui/switch";
import { fmtNum } from "@/lib/format";
import { Search, X, Globe2, CircleDot, AlertTriangle, RefreshCw, Clock } from "lucide-react";

const QTY_PRESETS = [1, 5, 10, 25, 100];

// The order-entry ticket ("order form") for the global-markets page.
export const BrokerOrderTicket = ({ b }) => {
  const {
    query, results, selected, side, setSide, qty, setQty, sizeMode, setSizeMode,
    amountCash, setAmountCash, orderType, setOrderType, limitPrice, setLimitPrice,
    takeProfit, setTakeProfit, stopLoss, setStopLoss, trailPercent, setTrailPercent,
    autoFx, setAutoFx, busy, selMarket, notionalMode, estCostUsd, status,
    search, pick, clearSel, openReview,
  } = b;

  return (
    <Card className="p-5 shadow-premium h-fit" data-testid="broker-ticket">
      <div className="text-sm font-medium mb-4">Order Ticket</div>
      <div className="space-y-4">
        <div className="space-y-1.5">
          <Label>Instrument</Label>
          <div className="relative">
            <Search className="h-4 w-4 absolute start-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input className="ps-9 pe-9" value={query} onChange={(e) => search(e.target.value)} placeholder="AAPL, MSFT, VOO..." data-testid="broker-search-input" />
            {(query || selected) && <button onClick={clearSel} className="absolute end-2.5 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"><X className="h-4 w-4" /></button>}
          </div>
          {results.length > 0 && (
            <div className="mt-1 rounded-lg border bg-popover shadow-md overflow-hidden" data-testid="broker-search-results">
              {results.map((r, i) => (
                <button key={`${r.symbol}-${i}`} onClick={() => pick(r)} className="w-full text-start px-3 py-2 hover:bg-accent transition-colors flex items-center justify-between gap-2" data-testid={`broker-result-${r.symbol}`}>
                  <span className="min-w-0"><span className="font-medium text-sm">{r.symbol}</span><span className="text-xs text-muted-foreground ms-2 truncate">{r.name}</span></span>
                  <span className="numeric text-xs text-muted-foreground">{fmtNum(r.price)}</span>
                </button>
              ))}
            </div>
          )}
        </div>

        {selected && (
          <div className="rounded-lg bg-secondary/60 p-3 space-y-2" data-testid="broker-selected">
            <div className="flex items-center justify-between">
              <div><div className="font-medium text-sm">{selected.symbol}</div><div className="text-xs text-muted-foreground truncate max-w-[150px]">{selected.name}</div></div>
              <div className="text-end"><div className="text-[11px] text-muted-foreground">Last</div><div className="numeric font-semibold">{fmtNum(selected.price)} {selected.currency}</div></div>
            </div>
            <div className="flex items-center justify-between gap-2 border-t pt-2">
              <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                <Globe2 className="h-3.5 w-3.5" />{selMarket?.name || selected.market}
              </span>
              {selMarket && (
                <span className={`inline-flex items-center gap-1 text-[11px] font-medium ${selMarket.is_open ? "text-emerald-600" : "text-muted-foreground"}`}
                  data-testid="broker-selected-market-status">
                  <CircleDot className="h-3 w-3" />{selMarket.is_open ? "Open" : "Closed"}
                  {selMarket.local_time ? ` · ${selMarket.local_time}` : ""}
                </span>
              )}
            </div>
            {selected.currency && selected.currency !== "USD" && (
              <>
                <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground border-t pt-2">
                  <AlertTriangle className="h-3.5 w-3.5 text-[color:var(--tharwy-lime-700)] shrink-0" />
                  Priced in {selected.currency}
                  {selected.price_usd ? ` (≈ ${fmtNum(selected.price_usd)} USD)` : ""}.
                </div>
                {side === "buy" && (
                  <div className="flex items-center justify-between gap-2 rounded-lg bg-background/60 border p-2" data-testid="broker-autofx-row">
                    <div className="flex items-center gap-1.5 text-[11px] text-muted-foreground pe-1">
                      <RefreshCw className="h-3.5 w-3.5 text-[color:var(--tharwy-lime-700)] shrink-0" />
                      Auto-convert USD → {selected.currency} if my balance is short
                    </div>
                    <Switch checked={autoFx} onCheckedChange={setAutoFx} data-testid="broker-autofx-switch" />
                  </div>
                )}
              </>
            )}
          </div>
        )}

        <div className="grid grid-cols-2 gap-2">
          <Button variant={side === "buy" ? "default" : "outline"} onClick={() => setSide("buy")} data-testid="broker-side-buy">Buy</Button>
          <Button variant={side === "sell" ? "default" : "outline"} onClick={() => setSide("sell")} data-testid="broker-side-sell">Sell</Button>
        </div>

        {side === "buy" && (
          <div className="inline-flex rounded-lg border p-0.5 text-xs" data-testid="broker-size-mode">
            <button onClick={() => setSizeMode("shares")} className={`px-3 py-1 rounded-md transition-colors ${sizeMode === "shares" ? "bg-secondary text-foreground" : "text-muted-foreground"}`} data-testid="broker-size-shares">Shares</button>
            <button onClick={() => setSizeMode("amount")} className={`px-3 py-1 rounded-md transition-colors ${sizeMode === "amount" ? "bg-secondary text-foreground" : "text-muted-foreground"}`} data-testid="broker-size-amount">$ Amount</button>
          </div>
        )}

        {notionalMode() ? (
          <div className="space-y-1.5">
            <Label>Amount (USD)</Label>
            <div className="relative">
              <span className="absolute start-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">$</span>
              <Input type="number" min="1" step="1" value={amountCash} onChange={(e) => setAmountCash(e.target.value)} className="ps-7" data-testid="broker-amount-input" />
            </div>
            <p className="text-[11px] text-muted-foreground">Buy a dollar amount — fractional shares supported (DriveWealth-style). Market orders only.</p>
            <div className="flex flex-wrap gap-1.5">
              {[25, 50, 100, 250, 500].map((n) => (
                <button key={n} onClick={() => setAmountCash(n)}
                  className={`px-2.5 py-1 rounded-md text-xs font-medium border transition-colors ${Number(amountCash) === n ? "bg-secondary border-[color:var(--tharwy-lime)] text-foreground" : "text-muted-foreground hover:bg-accent"}`}
                  data-testid={`broker-amount-preset-${n}`}>${n}</button>
              ))}
            </div>
          </div>
        ) : (
          <>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1.5"><Label>Quantity</Label><Input type="number" min="0" value={qty} onChange={(e) => setQty(e.target.value)} data-testid="broker-qty-input" /></div>
              <div className="space-y-1.5"><Label>Type</Label>
                <Select value={orderType} onValueChange={setOrderType}>
                  <SelectTrigger data-testid="broker-order-type"><SelectValue /></SelectTrigger>
                  <SelectContent><SelectItem value="market">Market</SelectItem><SelectItem value="limit">Limit</SelectItem></SelectContent>
                </Select>
              </div>
            </div>
            <div className="flex flex-wrap gap-1.5" data-testid="broker-qty-presets">
              {QTY_PRESETS.map((n) => (
                <button key={n} onClick={() => setQty(n)}
                  className={`px-2.5 py-1 rounded-md text-xs font-medium border transition-colors ${Number(qty) === n ? "bg-secondary border-[color:var(--tharwy-lime)] text-foreground" : "text-muted-foreground hover:bg-accent"}`}
                  data-testid={`broker-qty-preset-${n}`}>{n}</button>
              ))}
            </div>
            {orderType === "limit" && (
              <div className="space-y-1.5"><Label>Limit price</Label><Input type="number" value={limitPrice} onChange={(e) => setLimitPrice(e.target.value)} data-testid="broker-limit-input" /></div>
            )}
          </>
        )}
        {selected && (
          <div className="rounded-lg bg-secondary/60 p-2.5 flex items-center justify-between text-sm" data-testid="broker-est-cost">
            <span className="text-muted-foreground">Est. {side === "buy" ? "cost" : "proceeds"}</span>
            <span className="numeric font-semibold">{fmtNum(estCostUsd)} {selected.currency || "USD"}</span>
          </div>
        )}

        {side === "buy" && (
          <div className="space-y-2 rounded-lg border p-3" data-testid="broker-bracket">
            <div className="text-xs font-medium text-muted-foreground">Bracket (optional)</div>
            <div className="grid grid-cols-2 gap-3">
              <div className="space-y-1"><Label className="text-xs text-emerald-600">Take profit</Label>
                <Input type="number" value={takeProfit} onChange={(e) => setTakeProfit(e.target.value)} placeholder="price" data-testid="broker-tp-input" /></div>
              <div className="space-y-1"><Label className="text-xs text-red-600">Stop loss</Label>
                <Input type="number" value={stopLoss} onChange={(e) => setStopLoss(e.target.value)} placeholder="price" data-testid="broker-sl-input" /></div>
            </div>
            <div className="space-y-1">
              <Label className="text-xs text-[color:var(--tharwy-lime-700)]">Trailing stop</Label>
              <div className="relative">
                <Input type="number" min="0" step="0.5" value={trailPercent}
                  onChange={(e) => setTrailPercent(e.target.value)} placeholder="e.g. 8" className="pe-8"
                  data-testid="broker-trail-input" />
                <span className="absolute end-3 top-1/2 -translate-y-1/2 text-sm text-muted-foreground">%</span>
              </div>
              <p className="text-[11px] text-muted-foreground">
                Sell-stop that trails {trailPercent || 0}% below the high-water mark to lock in gains.
                {stopLoss && trailPercent ? " Both a fixed stop and a trailing stop will be placed." : ""}
              </p>
            </div>
          </div>
        )}

        {selected && selMarket && !selMarket.is_open && (
          <div className="rounded-lg border border-amber-500/30 bg-amber-500/10 p-2.5 flex gap-2 text-xs" data-testid="broker-market-closed-warning">
            <Clock className="h-4 w-4 text-amber-600 shrink-0 mt-0.5" />
            <span className="text-muted-foreground">
              {selMarket.name} is closed. Your order will be <span className="font-medium text-foreground">queued</span> and fill at the next open ({selMarket.next_event || selMarket.hours}).
            </span>
          </div>
        )}

        <Button className="w-full" onClick={openReview} disabled={busy || !selected || (notionalMode() ? Number(amountCash) <= 0 : (Number(qty) <= 0 || (orderType === "limit" && !limitPrice)))} data-testid="broker-review">
          {selected && selMarket && !selMarket.is_open ? "Queue order" : "Review order"}
        </Button>
        <p className="text-[11px] text-muted-foreground">Routed to our global venue ({status?.mode}). Market orders fill immediately; limit orders rest until marketable.</p>
      </div>
    </Card>
  );
};
